import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Upload, X, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api } from "@/lib/api";
import { trackQuoteSubmit } from "@/lib/analytics";
import { SERVICES, PROPERTY_TYPES, COMPANY } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import { useSeo } from "@/hooks/useSeo";

const CONTACT_METHODS = ["Phone", "Email", "WhatsApp"];
const BEDROOMS = ["1", "2", "3", "4", "5+"];

export default function Quote() {
  useSeo(
    "Request a Free Quote | Exterior Cleaning Lancashire | StoneCrest",
    "Request a free, fixed-price quote for exterior cleaning across Lancashire — roof cleaning, render softwashing, driveway and patio cleaning, gutter clearing and more. Residential and commercial.",
    "https://stonecrestexteriors.co.uk/quote"
  );
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", property_category: "Residential", property_type: "", bedrooms: "",
    services: [], notes: "", photo_urls: [], preferred_contact: "Phone", source: "quote", website: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (name) => set("services", form.services.includes(name) ? form.services.filter((s) => s !== name) : [...form.services, name]);

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("photo_urls", [...form.photo_urls, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch (e) {
      toast.error("Photo upload failed. You can still submit without photos.");
    } finally {
      setUploading(false);
    }
  };

  const step0Valid = form.name && form.phone && form.email && form.address;
  const step1Valid = form.services.length > 0 && (form.property_category === "Commercial" || form.property_type);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      trackQuoteSubmit();
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error("Sorry, we couldn't submit your request just now. Please call us on " + COMPANY.phone + " or try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="px-6 md:px-12 max-w-3xl mx-auto py-32 text-center">
        <Toaster position="top-center" />
        <div className="w-20 h-20 rounded-full bg-[var(--oxblood)] text-white flex items-center justify-center mx-auto mb-8"><Check className="w-10 h-10" /></div>
        <h1 className="font-serif font-light text-4xl md:text-6xl mb-6" data-testid="quote-success">Thank you, {form.name.split(" ")[0]}.</h1>
        <p className="text-[var(--muted)] text-lg leading-relaxed">Thank you — we've received your request. A member of the StoneCrest team will be in touch.</p>
        <p className="mt-4 text-[var(--muted)]">Need us sooner? Call <a href={COMPANY.phoneHref} className="text-[var(--oxblood)] underline">{COMPANY.phone}</a>.</p>
      </div>
    );
  }

  const steps = ["Your Details", "Property & Services", "Confirm"];

  return (
    <div>
      <Toaster position="top-center" />
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website", e.target.value)} className="absolute left-[-9999px] w-0 h-0 opacity-0" />
      <PageHeader overline="Free Quote" title={<>Get your <span className="italic text-[var(--oxblood)]">fixed-price</span> quote.</>} subtitle="Tell us about your residential or commercial property and the services you need. It takes under a minute." />

      <section className="px-6 md:px-12 max-w-3xl mx-auto pb-24 md:pb-32">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-12">
          {steps.map((s, i) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-display transition-colors ${i <= step ? "bg-[var(--oxblood)] text-white" : "bg-[var(--bone)] text-[var(--muted)]"}`}>{i + 1}</div>
              <span className={`text-xs uppercase tracking-wide hidden sm:block ${i === step ? "text-[var(--ink)]" : "text-[var(--muted)]"}`}>{s}</span>
              {i < steps.length - 1 && <span className="flex-1 h-px bg-[var(--border)]" />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
              <Field label="Full Name *"><input data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Jane Smith" /></Field>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Phone Number *"><input data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="07…" /></Field>
                <Field label="Email *"><input data-testid="quote-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="jane@email.com" /></Field>
              </div>
              <Field label="Property Address *"><input data-testid="quote-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} placeholder="123 Example Road, Preston, PR1…" /></Field>
              <div className="flex justify-end pt-4">
                <button data-testid="quote-next-0" disabled={!step0Valid} onClick={() => setStep(1)} className="bg-[var(--oxblood)] text-white px-8 py-4 tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors disabled:opacity-40 flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
              <Field label="Is this for a residential or commercial property? *">
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {["Residential", "Commercial"].map((c) => (
                    <button key={c} data-testid={`property-category-${c.toLowerCase()}`} onClick={() => setForm((f) => ({ ...f, property_category: c, ...(c === "Commercial" ? { property_type: "", bedrooms: "" } : {}) }))} className={`py-3 text-sm border transition-colors ${form.property_category === c ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{c}</button>
                  ))}
                </div>
              </Field>
              {form.property_category === "Residential" && (
                <>
                  <Field label="Property Type *">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {PROPERTY_TYPES.map((t) => (
                        <button key={t} data-testid={`property-${t.toLowerCase()}`} onClick={() => set("property_type", t)} className={`py-3 text-sm border transition-colors ${form.property_type === t ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{t}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Bedrooms">
                    <div className="flex flex-wrap gap-3">
                      {BEDROOMS.map((b) => (
                        <button key={b} onClick={() => set("bedrooms", b)} className={`w-14 py-3 text-sm border transition-colors ${form.bedrooms === b ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{b}</button>
                      ))}
                    </div>
                  </Field>
                </>
              )}
              <Field label="Services Required *">
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => (
                    <button key={s.slug} data-testid={`service-check-${s.slug}`} onClick={() => toggleService(s.name)} className={`flex items-center gap-3 p-4 text-left text-sm border transition-colors ${form.services.includes(s.name) ? "border-[var(--oxblood)] bg-[var(--oxblood-light)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>
                      <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${form.services.includes(s.name) ? "bg-[var(--oxblood)] border-[var(--oxblood)]" : "border-[var(--border)]"}`}>{form.services.includes(s.name) && <Check className="w-3.5 h-3.5 text-white" />}</span>
                      {s.name}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(0)} className="border border-[var(--ink)] px-8 py-4 tracking-wide hover:bg-[var(--ink)] hover:text-white transition-all flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button data-testid="quote-next-1" disabled={!step1Valid} onClick={() => setStep(2)} className="bg-[var(--oxblood)] text-white px-8 py-4 tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors disabled:opacity-40 flex items-center gap-2">Continue <ArrowRight className="w-4 h-4" /></button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-8">
              <Field label="Additional Notes"><textarea data-testid="quote-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} className={inputCls} placeholder="Anything else we should know?" /></Field>

              <Field label="Photos (optional)">
                <label className="flex items-center justify-center gap-3 border border-dashed border-[var(--border)] py-8 cursor-pointer hover:border-[var(--oxblood)] transition-colors">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[var(--oxblood)]" /> : <Upload className="w-5 h-5 text-[var(--oxblood)]" />}
                  <span className="text-sm text-[var(--muted)]">{uploading ? "Uploading…" : "Upload photos of your property"}</span>
                  <input type="file" data-testid="quote-photo-upload" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && uploadPhotos(Array.from(e.target.files))} />
                </label>
                {form.photo_urls.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.photo_urls.map((u, i) => (
                      <div key={i} className="relative w-16 h-16 bg-[var(--bone)] flex items-center justify-center">
                        <Check className="w-5 h-5 text-[var(--oxblood)]" />
                        <button onClick={() => set("photo_urls", form.photo_urls.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-[var(--ink)] text-white rounded-full w-5 h-5 flex items-center justify-center"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Field>

              <Field label="Preferred Contact Method">
                <div className="flex gap-3">
                  {CONTACT_METHODS.map((m) => (
                    <button key={m} onClick={() => set("preferred_contact", m)} className={`flex-1 py-3 text-sm border transition-colors ${form.preferred_contact === m ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{m}</button>
                  ))}
                </div>
              </Field>

              <div className="bg-[var(--bone)] p-6 text-sm space-y-1">
                <p><span className="text-[var(--muted)]">Enquiry type:</span> {form.property_category}</p>
                <p><span className="text-[var(--muted)]">Services:</span> {form.services.join(", ") || "—"}</p>
                <p><span className="text-[var(--muted)]">Property:</span> {form.property_type || (form.property_category === "Commercial" ? "Commercial property" : "—")} {form.bedrooms && `· ${form.bedrooms} bed`}</p>
              </div>

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="border border-[var(--ink)] px-8 py-4 tracking-wide hover:bg-[var(--ink)] hover:text-white transition-all flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button data-testid="quote-submit" disabled={submitting} onClick={submit} className="bg-[var(--oxblood)] text-white px-10 py-4 tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors disabled:opacity-40 flex items-center gap-2">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Submit Quote Request"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}

const inputCls = "w-full border border-[var(--border)] px-4 py-3.5 text-[var(--ink)] focus:outline-none focus:border-[var(--oxblood)] transition-colors bg-white";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-3">{label}</label>
    {children}
  </div>
);
